import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wrench, ArrowLeft, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const rules = useMemo(() => ([
    { key: "len",     label: "At least 8 characters",  ok: password.length >= 8 },
    { key: "upper",   label: "One uppercase letter",   ok: /[A-Z]/.test(password) },
    { key: "number",  label: "One number",             ok: /\d/.test(password) },
    { key: "special", label: "One special character",  ok: /[^A-Za-z0-9]/.test(password) },
  ]), [password]);
  const passwordOk = rules.every((r) => r.ok);
  const matches = password.length > 0 && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token");
      return;
    }
    if (!passwordOk) {
      toast.error("Please meet all password requirements");
      return;
    }
    if (!matches) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — sign in with your new password.");
      setTimeout(() => navigate("/auth?mode=login", { replace: true }), 1400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid md:grid-cols-2">
      <div className="p-6 md:p-12 flex flex-col justify-between bg-grain">
        <Link to="/auth" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors w-fit" data-testid="back-auth-link">
          <ArrowLeft weight="bold" className="w-4 h-4" /> Back to sign in
        </Link>

        <div className="max-w-md w-full mx-auto py-10 fade-up">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
              <Wrench weight="bold" className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-xl leading-none">DYNAMIC DIAG</div>
              <div className="text-[10px] font-mono-tech text-muted-foreground tracking-widest">AI DIAGNOSTIC ENGINE</div>
            </div>
          </div>

          {!token ? (
            <>
              <h1 className="font-display font-bold text-4xl tracking-tight">Missing token</h1>
              <p className="text-sm text-muted-foreground mt-2">
                This reset link isn't valid. Head back to sign in and tap "Forgot password?" to send a fresh one.
              </p>
              <Button asChild className="mt-6 bg-primary hover:bg-primary/90 min-h-12">
                <Link to="/auth?mode=login" data-testid="back-to-signin-btn">Back to sign in</Link>
              </Button>
            </>
          ) : done ? (
            <div className="border border-green-500/40 bg-green-500/5 rounded-md p-6" data-testid="reset-success">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle weight="fill" className="w-5 h-5" />
                <div className="font-display text-2xl">Password updated</div>
              </div>
              <p className="text-sm text-white/80 mt-2">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="font-display font-bold text-4xl tracking-tight">Set a new password</h1>
              <p className="text-sm text-muted-foreground mt-2">Pick something strong — you'll use it every day.</p>

              <form onSubmit={submit} className="space-y-4 mt-6" autoComplete="off">
                <div>
                  <Label htmlFor="pw" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">New password</Label>
                  <Input
                    id="pw"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1 min-h-12 bg-input border-border"
                    data-testid="reset-password-input"
                  />
                  {password.length > 0 && (
                    <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid="reset-password-rules">
                      {rules.map((r) => (
                        <li
                          key={r.key}
                          data-testid={`reset-pw-rule-${r.key}`}
                          className={`text-[11px] font-mono-tech flex items-center gap-1.5 ${r.ok ? "text-green-400" : "text-muted-foreground"}`}
                        >
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${r.ok ? "bg-green-400" : "bg-muted-foreground/50"}`} />
                          {r.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <Label htmlFor="confirm" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Confirm new password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1 min-h-12 bg-input border-border"
                    data-testid="reset-confirm-input"
                  />
                  {confirm.length > 0 && !matches && (
                    <div className="mt-1 text-[11px] font-mono-tech text-destructive" data-testid="reset-mismatch">
                      Passwords don't match
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !passwordOk || !matches}
                  className="w-full min-h-12 bg-primary hover:bg-primary/90"
                  data-testid="reset-submit-btn"
                >
                  {submitting ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="text-xs text-muted-foreground font-mono-tech">© Dynamic Diag</div>
      </div>

      <div className="hidden md:block relative border-l border-border">
        <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/40 to-background" />
        <div className="relative h-full flex flex-col justify-center items-center p-12">
          <div className="border border-border rounded-md p-6 bg-background/70 backdrop-blur max-w-md text-center">
            <div className="font-mono-tech text-xs text-primary mb-2">// SECURE RESET</div>
            <div className="font-display text-xl leading-tight">
              One-time link · expires in 60 minutes · logs you out on every device once used.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
